import React, { useCallback, useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  Drawer,
  FormControl,
  Input,
  List as MUIList,
  ListItemButton,
  MenuItem,
  Select,
  Switch,
  Tooltip,
  Typography,
  CircularProgress,
} from "@mui/material";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import { useUIState } from "../../context/UIStateContext";
import { useDraftData } from "../../context/DraftDataContext";
import { TOURNAMENT_GROUP_LABELS } from "../../helpers/tournamentHelpers";
import { useAdp } from "../../context/AdpContext";
import { brand, surfaceTokens } from "../../config/theme";

const DRAWER_WIDTH = 260;

const NAV_ITEMS = [
  { label: "Dashboard",  to: "/dashboard",  Icon: DashboardOutlinedIcon },
  { label: "My Players", to: "/players",    Icon: PeopleOutlinedIcon },
  { label: "My Teams",   to: "/teams",      Icon: GroupsOutlinedIcon },
  { label: "Insights",   to: "/insights",   Icon: InsightsOutlinedIcon },
  { label: "ADP Trends", to: "/adp-trends", Icon: TrendingUpOutlinedIcon },
  { label: "Schedule",   to: "/schedule",   Icon: CalendarMonthOutlinedIcon },
  { label: "Season Wrapped", to: "/wrapped", Icon: AutoAwesomeOutlinedIcon },
];

// ── Sidebar divider ───────────────────────────────────────────────

function SidebarDivider() {
  return (
    <Box
      component="hr"
      sx={{
        border: "none",
        borderTop: "1px solid",
        borderColor: "divider",
        mx: 2,
        my: 0,
      }}
    />
  );
}

// ── Upload time helper ────────────────────────────────────────────

function formatUploadedAgo(date: Date): string {
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

// ── Compact upload zone ───────────────────────────────────────────

function SidebarUploadZone() {
  const {
    fileName,
    error,
    isParsing,
    handleFile,
    clearData,
    tournamentOptions,
    tournamentGroups,
    tournamentFilter,
    setTournamentFilter,
    uploadedAt,
    playerCount,
    teamCount,
    providersPresent,
    providerDraftCounts,
    hasMultipleProviders,
    clearProvider,
    importMode,
    setImportMode,
  } = useDraftData();
  const hasUnderdog = providersPresent.includes("Underdog");
  const knownProviders = providersPresent.filter(p => p !== "Unknown");

  const {
    snapshotDates,
    snapshotDate,
    snapshotAdpLoading,
    adpError,
    historyError,
    scheduleError,
    retryAdp,
    setSnapshotDate,
  } = useAdp();

  // The snapshot series is Underdog-only. If NO Underdog picks are loaded (pure
  // DraftKings) while a historical UD snapshot is still selected, the picker is
  // hidden but the stale date would keep driving CLV — so pin back to live.
  useEffect(() => {
    if (!hasUnderdog && snapshotDate !== "live") {
      setSnapshotDate("live");
    }
  }, [hasUnderdog, snapshotDate, setSnapshotDate]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];
      if (!file) return;
      const syntheticEvent = {
        target: { files: [file] } as unknown as HTMLInputElement,
      } as React.ChangeEvent<HTMLInputElement>;
      handleFile(syntheticEvent);
    },
    [handleFile]
  );

  const primaryColor = brand.primary.light;

  return (
    <Box sx={{ px: 2, py: 1.5 }}>
      {/* Import mode — when data is already loaded, choose whether the next file
          adds/refreshes a platform (merge) or replaces the whole portfolio. */}
      {playerCount > 0 && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
          <Typography variant="caption" sx={{ color: "text.secondary", fontSize: 10, mr: 0.25 }}>
            Next import:
          </Typography>
          {(["merge", "replace"] as const).map(mode => (
            <Chip
              key={mode}
              size="small"
              label={mode === "merge" ? "Add" : "Replace"}
              onClick={() => setImportMode(mode)}
              aria-pressed={importMode === mode}
              color={importMode === mode ? "primary" : "default"}
              variant={importMode === mode ? "filled" : "outlined"}
              sx={{ height: 20, fontSize: 10, fontWeight: 600, cursor: "pointer" }}
            />
          ))}
        </Box>
      )}
      {/* Drop zone */}
      <Box
        onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        sx={{
          border: "1.5px dashed",
          borderColor: isDragging ? "primary.main" : "divider",
          borderRadius: 2,
          p: 1.5,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 0.5,
          cursor: "pointer",
          bgcolor: isDragging ? `${primaryColor}08` : "transparent",
          transition: "border-color 0.15s, background-color 0.15s",
          "&:hover": {
            borderColor: "primary.main",
            bgcolor: `${primaryColor}06`,
          },
        }}
      >
        <Input
          inputRef={fileInputRef}
          type="file"
          inputProps={{ accept: ".csv" }}
          onChange={handleFile}
          sx={{ display: "none" }}
        />
        {isParsing ? (
          <CircularProgress size={18} sx={{ my: 0.5 }} />
        ) : (
          <UploadFileOutlinedIcon sx={{ fontSize: 20, color: "text.secondary" }} />
        )}
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", textAlign: "center", lineHeight: 1.3 }}
        >
          {isParsing ? "Parsing…" : "Drop CSV here or click to browse"}
        </Typography>
      </Box>

      {/* Data status indicator */}
      {fileName && !isParsing && (
        <Box
          sx={{
            mt: 1.5,
            p: 1.25,
            bgcolor: `${primaryColor}08`,
            border: "1px solid",
            borderColor: `${primaryColor}30`,
            borderRadius: 2,
            animation: "statusSlideIn 0.35s ease-out",
            "@keyframes statusSlideIn": {
              from: { opacity: 0, transform: "translateY(-8px)" },
              to:   { opacity: 1, transform: "translateY(0)" },
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75, mb: 0.5 }}>
            <CheckCircleOutlineIcon sx={{
              fontSize: 14,
              color: "success.main",
              flexShrink: 0,
              mt: "2px",
              animation: "checkPop 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
              "@keyframes checkPop": {
                from: { transform: "scale(0)", opacity: 0 },
                to:   { transform: "scale(1)", opacity: 1 },
              },
            }} />
            <Typography
              variant="caption"
              sx={{
                color: "text.primary",
                fontWeight: 600,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                flex: 1,
                fontSize: 11,
              }}
              title={fileName}
            >
              {fileName}
            </Typography>
          </Box>
          {(playerCount > 0 || teamCount > 0) && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", pl: 2.5, fontSize: 11 }}>
              {playerCount} players · {teamCount} drafts
            </Typography>
          )}
          {uploadedAt && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", pl: 2.5, fontSize: 11 }}>
              Uploaded {formatUploadedAgo(uploadedAt)}
            </Typography>
          )}
          <Box sx={{ display: "flex", gap: 1, mt: 1, pl: 2.5 }}>
            <Typography
              variant="caption"
              component="span"
              onClick={() => { clearData(); if (fileInputRef.current) fileInputRef.current.value = ""; }}
              sx={{
                color: "text.disabled",
                cursor: "pointer",
                fontSize: 11,
                "&:hover": { color: "error.main" },
                textDecoration: "underline",
              }}
            >
              Clear
            </Typography>
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: 11 }}>·</Typography>
            <Typography
              variant="caption"
              component="span"
              onClick={() => fileInputRef.current?.click()}
              sx={{
                color: primaryColor,
                cursor: "pointer",
                fontSize: 11,
                "&:hover": { opacity: 0.8 },
                textDecoration: "underline",
              }}
            >
              Re-upload
            </Typography>
          </Box>
        </Box>
      )}

      {/* Loaded platforms — remove one without wiping the other */}
      {hasMultipleProviders && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 0.5, fontSize: 11 }}>
            Loaded platforms
          </Typography>
          {knownProviders.map(prov => (
            <Box
              key={prov}
              sx={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                py: 0.5, px: 1, mb: 0.5, borderRadius: 1.5,
                border: "1px solid", borderColor: "divider",
              }}
            >
              <Typography variant="caption" sx={{ fontSize: 11, fontWeight: 600 }}>
                {prov}
                <Typography component="span" variant="caption" sx={{ color: "text.secondary", fontWeight: 400, ml: 0.5 }}>
                  · {providerDraftCounts[prov] ?? 0} drafts
                </Typography>
              </Typography>
              <Typography
                component="span"
                variant="caption"
                onClick={() => clearProvider(prov)}
                sx={{ color: "text.disabled", cursor: "pointer", fontSize: 11, "&:hover": { color: "error.main" }, textDecoration: "underline" }}
              >
                Remove
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 1, py: 0.25, fontSize: 11 }}>
          {error}
        </Alert>
      )}

      {adpError && (
        <Alert
          severity="warning"
          sx={{ mt: 1, py: 0.25, fontSize: 11 }}
          action={
            <Button color="inherit" size="small" onClick={retryAdp} sx={{ fontSize: 10, minWidth: 0 }}>
              Retry
            </Button>
          }
        >
          ADP unavailable
        </Alert>
      )}

      {historyError && (
        <Alert severity="info" sx={{ mt: 1, py: 0.25, fontSize: 11 }}>
          ADP history unavailable
        </Alert>
      )}

      {scheduleError && (
        <Alert severity="info" sx={{ mt: 1, py: 0.25, fontSize: 11 }}>
          Schedule data unavailable
        </Alert>
      )}

      {/* ADP snapshot selector — shown whenever Underdog picks are present (the
          snapshots are a UD dated series). Hidden only for a pure DraftKings
          portfolio, which has just a current ADP and no history. Applies to the
          UD slice of a mixed portfolio. */}
      {snapshotDates.length > 0 && hasUnderdog && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 0.5 }}>
            ADP reference date
          </Typography>
          <FormControl size="small" fullWidth>
            <Select
              value={snapshotDate}
              onChange={e => setSnapshotDate(e.target.value)}
              disabled={snapshotAdpLoading}
              sx={{ fontSize: 12 }}
            >
              <MenuItem value="live" sx={{ fontSize: 12 }}>Current (Live)</MenuItem>
              {snapshotDates.map(d => (
                <MenuItem key={d} value={d} sx={{ fontSize: 12 }}>{d}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {/* Tournament filter — grouped by pre-draft vs regular season */}
      {tournamentOptions.length > 1 && (
        <Box sx={{ mt: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Tournaments
            </Typography>
            <Typography
              variant="caption"
              onClick={() => setTournamentFilter(
                tournamentFilter.length === tournamentOptions.length ? [] : tournamentOptions,
              )}
              sx={{ color: "primary.main", cursor: "pointer", fontWeight: 600 }}
            >
              {tournamentFilter.length === tournamentOptions.length ? "Clear all" : "All"}
            </Typography>
          </Box>

          {(["pre", "regular", "undated"] as const).map(key => {
            const titles = tournamentGroups[key];
            if (!titles.length) return null;
            const allSelected = titles.every(t => tournamentFilter.includes(t));
            const toggleGroup = () =>
              setTournamentFilter(
                allSelected
                  ? tournamentFilter.filter(t => !titles.includes(t))
                  : Array.from(new Set([...tournamentFilter, ...titles])),
              );
            // Only label the group when more than one group is present.
            const showGroupHeader = [tournamentGroups.pre, tournamentGroups.regular, tournamentGroups.undated]
              .filter(g => g.length).length > 1;
            return (
              <Box key={key} sx={{ mb: 1 }}>
                {showGroupHeader && (
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.4 }}>
                      {TOURNAMENT_GROUP_LABELS[key]}
                    </Typography>
                    <Typography
                      variant="caption"
                      onClick={toggleGroup}
                      sx={{ color: "primary.main", cursor: "pointer", fontSize: 10 }}
                    >
                      {allSelected ? "Clear" : "All"}
                    </Typography>
                  </Box>
                )}
                <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                  {titles.map(t => {
                    const isSolo = tournamentFilter.length === 1 && tournamentFilter[0] === t;
                    return (
                      <Tooltip
                        key={t}
                        title={isSolo ? "Click to show all tournaments" : "Click: only this tournament · Shift-click: add/remove"}
                        enterDelay={700}
                        arrow
                      >
                        <Chip
                          label={t}
                          size="small"
                          variant={tournamentFilter.includes(t) ? "filled" : "outlined"}
                          onClick={e => {
                            if (e.shiftKey || e.metaKey || e.ctrlKey) {
                              // Modifier click: classic multi-select toggle.
                              setTournamentFilter(
                                tournamentFilter.includes(t)
                                  ? tournamentFilter.filter(x => x !== t)
                                  : [...tournamentFilter, t],
                              );
                            } else if (isSolo) {
                              // Clicking the soloed tournament restores everything.
                              setTournamentFilter(tournamentOptions);
                            } else {
                              // Plain click: view only this tournament.
                              setTournamentFilter([t]);
                            }
                          }}
                          sx={{ fontSize: 11, height: 22 }}
                        />
                      </Tooltip>
                    );
                  })}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

    </Box>
  );
}

// ── Main sidebar ──────────────────────────────────────────────────

interface AppSidebarProps {
  onNavigate: () => void;
  isMobile?: boolean;
  mobileOpen?: boolean;
  onClose?: () => void;
}

export default function AppSidebar({
  onNavigate,
  isMobile = false,
  mobileOpen = false,
  onClose = () => {},
}: AppSidebarProps) {
  const location = useLocation();
  const { darkMode, setDarkMode } = useUIState();
  const c = surfaceTokens(darkMode);
  const { playerCount, teamCount } = useDraftData();

  const navBadge: Record<string, number> = {
    "/players": playerCount,
    "/teams": teamCount,
  };

  const primaryColor = darkMode ? brand.primary.dark : brand.primary.light;

  return (
    <Drawer
      variant={isMobile ? "temporary" : "permanent"}
      open={isMobile ? mobileOpen : undefined}
      onClose={isMobile ? onClose : undefined}
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: {
          width: DRAWER_WIDTH,
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Logo */}
      <Box sx={{ px: 2.5, pt: 2.5, pb: 2, flexShrink: 0 }}>
        <img
          src={process.env.PUBLIC_URL + "/Transparent Logo.png"}
          alt="rosterOS"
          style={{
            maxWidth: "108%",
            maxHeight: 66,
            display: "block",
            filter: darkMode ? "brightness(0) invert(1)" : "none",
          }}
        />
      </Box>

      <SidebarDivider />

      {/* Navigation */}
      <MUIList disablePadding sx={{ pt: 1, pb: 1, flexShrink: 0 }}>
        {NAV_ITEMS.map(({ label, to, Icon }) => {
          const isActive =
            location.pathname === to || location.pathname.startsWith(to + "/");
          const badge = navBadge[to] ?? 0;

          return (
            <ListItemButton
              key={to}
              component={NavLink}
              to={to}
              onClick={onNavigate}
              sx={{
                mx: 1,
                my: 0.25,
                px: 1.5,
                py: 0.875,
                borderRadius: 1.5,
                borderLeft: isActive
                  ? `3px solid ${c.accentLine}`
                  : "3px solid transparent",
                bgcolor: isActive ? c.accentSoft : "transparent",
                "&:hover": {
                  bgcolor: isActive ? c.accentSoft : "action.hover",
                },
                transition: "background-color 0.15s, border-left-color 0.15s",
              }}
            >
              <Icon
                sx={{
                  fontSize: 18,
                  mr: 1.25,
                  color: isActive ? c.brandLight : "text.secondary",
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: isActive ? 700 : 500,
                  color: isActive ? c.brandLight : "text.secondary",
                  flex: 1,
                }}
              >
                {label}
              </Typography>
              {badge > 0 && (
                <Chip
                  label={badge}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: 11,
                    fontWeight: 600,
                    bgcolor: `${primaryColor}20`,
                    color: primaryColor,
                    border: "none",
                    "& .MuiChip-label": { px: 0.75 },
                  }}
                />
              )}
            </ListItemButton>
          );
        })}
      </MUIList>

      <SidebarDivider />

      {/* Upload zone — scrollable area between nav and footer */}
      <Box sx={{ flex: 1, overflow: "auto" }}>
        <SidebarUploadZone />
      </Box>

      <SidebarDivider />

      {/* Theme toggle */}
      <Box
        sx={{ px: 2, py: 1.5, display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}
      >
        <LightModeOutlinedIcon
          sx={{ fontSize: 15, color: darkMode ? "text.disabled" : primaryColor }}
        />
        <Switch
          size="small"
          checked={darkMode}
          onChange={e => setDarkMode(e.target.checked)}
          inputProps={{ "aria-label": "Toggle dark mode" }}
        />
        <DarkModeOutlinedIcon
          sx={{ fontSize: 15, color: darkMode ? primaryColor : "text.disabled" }}
        />
        <Typography variant="caption" sx={{ color: "text.secondary", ml: 0.25 }}>
          {darkMode ? "Dark" : "Light"}
        </Typography>
      </Box>

      <SidebarDivider />

      {/* Footer links */}
      <Box sx={{ px: 2.5, py: 1.25, flexShrink: 0, display: "flex", flexDirection: "column", gap: 0.5 }}>
        <a
          href="https://rosteros.com"
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: "none" }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              "&:hover": { color: "text.primary" },
              transition: "color 0.15s",
            }}
          >
            ← rosteros.com
          </Typography>
        </a>
        <a
          href="https://rosteros.com/support-the-creators"
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: "none" }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              "&:hover": { color: "text.primary" },
              transition: "color 0.15s",
            }}
          >
            Support the Creators
          </Typography>
        </a>
      </Box>
    </Drawer>
  );
}
